// src/types/appointments/SchemaBuilderTypes.ts

/**
 * Base field types that can be used in the schema builder
 */
export type SchemaFieldType =
	| 'text'
	| 'number'
	| 'boolean'
	| 'enum'
	| 'array'
	| 'object'
	| 'section'
	| 'topic';

/**
 * Configuration for enum field types
 */
export interface EnumConfig {
	options: string[];
	allowCustom?: boolean;
}

/**
 * Configuration for array field types
 */
export interface ArrayConfig {
	itemType: SchemaFieldType;
	minItems?: number;
	maxItems?: number;
	// For array of objects, define the object structure
	itemSchema?: SchemaNode[];
}

/**
 * Base schema node that represents any field in the schema
 */
export interface SchemaNode {
	id: string; // Unique identifier for the node
	type: SchemaFieldType;
	key: string; // The JSON property key
	label: string; // Display label for practitioners
	description?: string; // Help text - CRITICAL: This gets saved to JSON Schema
	required?: boolean;
	placeholder?: string;

	// Type-specific configurations
	enumConfig?: EnumConfig;
	arrayConfig?: ArrayConfig;

	// For object/section/topic types - can contain children
	children?: SchemaNode[];

	// Visual organization
	order: number;
	parentId?: string;

	// Metadata for rendering
	metadata?: {
		icon?: string;
		color?: string;
		collapsible?: boolean;
		defaultCollapsed?: boolean;
	};
}

/**
 * A template for creating new schema nodes
 */
export interface SchemaNodeTemplate {
	type: SchemaFieldType;
	defaultLabel: string;
	icon: string;
	description: string;
	defaultConfig?: Partial<SchemaNode>;
}

/**
 * The complete schema structure for a workflow step
 */
export interface WorkflowStepSchema {
	rootNodes: SchemaNode[];
	version: string;
	metadata?: {
		generatedAt?: string;
		generatedBy?: string;
	};
}

/**
 * Value collected from a rendered schema during consultation
 */
export interface SchemaValue {
	[key: string]: any;
}

/**
 * Props for rendering a schema node during consultation
 */
export interface SchemaNodeRenderProps {
	node: SchemaNode;
	value?: any;
	onChange: (key: string, value: any) => void;
	readonly?: boolean;
	level?: number;
}

/**
 * Validation result for a schema value
 */
export interface ValidationResult {
	valid: boolean;
	errors: {
		path: string;
		message: string;
	}[];
}

/**
 * Available component templates for the palette
 */
export const COMPONENT_TEMPLATES: SchemaNodeTemplate[] = [
	{
		type: 'section',
		defaultLabel: 'New Section',
		icon: 'FolderOutlined',
		description: 'A top-level grouping for topics',
		defaultConfig: {
			metadata: { collapsible: true, defaultCollapsed: false }
		}
	},
	{
		type: 'topic',
		defaultLabel: 'New Topic',
		icon: 'FileTextOutlined',
		description: 'A discussion topic within a section',
		defaultConfig: {
			metadata: { collapsible: true, defaultCollapsed: false }
		}
	},
	{
		type: 'text',
		defaultLabel: 'Text Field',
		icon: 'FontSizeOutlined',
		description: 'Single line or multi-line text input'
	},
	{
		type: 'number',
		defaultLabel: 'Number Field',
		icon: 'NumberOutlined',
		description: 'Numeric value'
	},
	{
		type: 'boolean',
		defaultLabel: 'Yes/No Field',
		icon: 'CheckCircleOutlined',
		description: 'True/false checkbox'
	},
	{
		type: 'enum',
		defaultLabel: 'Choice Field',
		icon: 'UnorderedListOutlined',
		description: 'Select from predefined options',
		defaultConfig: {
			enumConfig: { options: ['Option 1', 'Option 2', 'Option 3'] }
		}
	},
	{
		type: 'array',
		defaultLabel: 'List Field',
		icon: 'OrderedListOutlined',
		description: 'Multiple items of the same type',
		defaultConfig: {
			arrayConfig: { itemType: 'text' }
		}
	},
	{
		type: 'object',
		defaultLabel: 'Group Field',
		icon: 'GroupOutlined',
		description: 'Nested group of fields'
	}
];

/**
 * Converts SchemaNode tree to JSON Schema format
 * UPDATED: Now preserves descriptions
 */
export function schemaNodeToJsonSchema(nodes: SchemaNode[]): any {
	const properties: any = {};
	const required: string[] = [];

	for (const node of nodes) {
		const baseSchema: any = {};

		// CRITICAL: Save description to JSON Schema
		if (node.description) {
			baseSchema.description = node.description;
		}

		if (node.type === 'section' || node.type === 'topic') {
			// Sections and topics become objects with their children as properties
			const childSchema = node.children ? schemaNodeToJsonSchema(node.children) : { properties: {} };
			properties[node.key] = {
				...baseSchema,
				type: 'object',
				title: node.label, // Preserve label
				properties: childSchema.properties,
				...(childSchema.required && childSchema.required.length > 0 ? {
					required: childSchema.required
				} : {})
			};
		} else if (node.type === 'array') {
			const itemsSchema = node.arrayConfig?.itemSchema
				? {
					type: 'object',
					properties: schemaNodeToJsonSchema(node.arrayConfig.itemSchema).properties
				}
				: { type: node.arrayConfig?.itemType || 'string' };

			properties[node.key] = {
				...baseSchema,
				type: 'array',
				title: node.label,
				items: itemsSchema,
				...(node.arrayConfig?.minItems && { minItems: node.arrayConfig.minItems }),
				...(node.arrayConfig?.maxItems && { maxItems: node.arrayConfig.maxItems })
			};
		} else if (node.type === 'enum') {
			properties[node.key] = {
				...baseSchema,
				type: 'string',
				title: node.label,
				enum: node.enumConfig?.options || []
			};
		} else if (node.type === 'object') {
			const childSchema = node.children ? schemaNodeToJsonSchema(node.children) : { properties: {} };
			properties[node.key] = {
				...baseSchema,
				type: 'object',
				title: node.label,
				properties: childSchema.properties,
				...(childSchema.required && childSchema.required.length > 0 ? {
					required: childSchema.required
				} : {})
			};
		} else {
			// Simple types
			properties[node.key] = {
				...baseSchema,
				type: node.type === 'text' ? 'string' : node.type,
				title: node.label
			};
		}

		if (node.required) {
			required.push(node.key);
		}
	}

	return {
		type: 'object',
		properties,
		...(required.length > 0 && { required })
	};
}

/**
 * Converts JSON Schema to SchemaNode tree for editing
 * UPDATED: Now extracts descriptions and titles
 */
export function jsonSchemaToSchemaNode(jsonSchema: any, parentId?: string): SchemaNode[] {
	// Handle null/undefined
	if (!jsonSchema) return [];

	// If the schema is not in proper JSON Schema format (missing type/properties),
	// try to auto-wrap it as an object with properties
	if (!jsonSchema.properties && jsonSchema.type !== 'object') {
		// Check if it looks like a raw object with keys
		if (typeof jsonSchema === 'object' && !Array.isArray(jsonSchema)) {
			console.warn('Schema is not in proper JSON Schema format, attempting to parse as properties:', jsonSchema);
			// Treat the object keys as properties
			jsonSchema = {
				type: 'object',
				properties: jsonSchema
			};
		} else {
			return [];
		}
	}

	if (!jsonSchema.properties) return [];

	const nodes: SchemaNode[] = [];
	let order = 0;

	for (const [key, schema] of Object.entries(jsonSchema.properties)) {
		const schemaObj = schema as any;
		order++;

		const node: SchemaNode = {
			id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${order}`,
			key,
			// Use title from schema if available, otherwise format the key
			label: schemaObj.title || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
			type: 'text',
			order,
			parentId,
			required: jsonSchema.required?.includes(key) || false,
			// CRITICAL: Extract description from JSON Schema
			description: schemaObj.description
		};

		// Determine type from schema
		if (schemaObj.type === 'object' && schemaObj.properties) {
			node.type = 'object';
			node.children = jsonSchemaToSchemaNode(schemaObj, node.id);
		} else if (schemaObj.type === 'array') {
			node.type = 'array';
			if (schemaObj.items?.type) {
				node.arrayConfig = {
					itemType: schemaObj.items.type as SchemaFieldType,
					minItems: schemaObj.minItems,
					maxItems: schemaObj.maxItems
				};
				if (schemaObj.items.type === 'object' && schemaObj.items.properties) {
					node.arrayConfig.itemSchema = jsonSchemaToSchemaNode(schemaObj.items, node.id);
				}
			}
		} else if (schemaObj.enum) {
			node.type = 'enum';
			node.enumConfig = { options: schemaObj.enum };
		} else if (schemaObj.type === 'string') {
			node.type = 'text';
		} else if (schemaObj.type === 'number' || schemaObj.type === 'integer') {
			node.type = 'number';
		} else if (schemaObj.type === 'boolean') {
			node.type = 'boolean';
		}

		nodes.push(node);
	}

	return nodes;
}